import * as THREE from "three";
import type { CameraConfig } from "../../cameraConfig";
import type { SceneControls } from "../controls/SceneControls";

export type CameraPose = {
  position: THREE.Vector3;
  target: THREE.Vector3;
  fov?: number;
};

export type OrbitAnimationConfig = {
  sightPosition: THREE.Vector3;
  distance: number;
  height: number;
  azimuthMin: number;
  azimuthMax: number;
  polarMin: number;
  polarMax: number;
};

export class CameraDirector {
  private camera: THREE.PerspectiveCamera;
  private controls: SceneControls;
  private cameraConfig: CameraConfig;
  private isTransitioning: boolean = false;
  private controlsWasEnabled: boolean = true;
  private transitionStartTime: number = 0;
  private transitionDuration: number = 0;
  private startPosition: THREE.Vector3 = new THREE.Vector3();
  private endPosition: THREE.Vector3 = new THREE.Vector3();
  private startTarget: THREE.Vector3 = new THREE.Vector3();
  private endTarget: THREE.Vector3 = new THREE.Vector3();
  private startFov: number = 0;
  private endFov: number = 0;
  private transitionApplyVerticalConstraint: boolean = true;
  private onComplete?: () => void;
  private lastPosePosition: THREE.Vector3 = new THREE.Vector3();
  private lastPoseTarget: THREE.Vector3 = new THREE.Vector3();
  private lastPoseFov: number = 0;
  private hasPose: boolean = false;

  private orbitAnimationEnabled: boolean = false;
  private orbitAnimationActive: boolean = false;
  private orbitAnimationStartTime: number = -1;
  private orbitAnimationBlendStartTime: number = -1;
  private orbitAnimationConfig: OrbitAnimationConfig | null = null;
  private orbitAnimationPosition: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationBlendedPosition: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationTarget: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationBlendStartTarget: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationBlendedTarget: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationBlendStartPosition: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationBlendDuration = 0.8;
  private orbitAnimationPhaseOffset = 0;

  private readonly orbitAnimationPeriod = 20;

  constructor(
    camera: THREE.PerspectiveCamera,
    controls: SceneControls,
    cameraConfig: CameraConfig
  ) {
    this.camera = camera;
    this.controls = controls;
    this.cameraConfig = { ...cameraConfig };
  }

  update(currentTime: number): void {
    if (this.isTransitioning) {
      this.updateTransition(currentTime);
      return;
    }

    if (
      this.orbitAnimationActive &&
      this.orbitAnimationConfig &&
      this.controls.viewMode === "orbit" &&
      this.controls.enabled
    ) {
      if (this.orbitAnimationStartTime < 0) {
        this.orbitAnimationStartTime = currentTime;
      }
      this.updateOrbitAnimation(currentTime);
    }
  }

  syncPoseFromCamera(): void {
    if (this.isTransitioning) {
      return;
    }
    this.lastPosePosition.copy(this.camera.position);
    if (this.controls.viewMode === "orbit") {
      this.lastPoseTarget.copy(this.controls.orbit.target);
    } else {
      const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(
        this.camera.quaternion
      );
      this.lastPoseTarget.copy(this.camera.position).add(direction);
    }
    this.lastPoseFov = this.camera.fov;
    this.hasPose = true;
  }

  isInTransition(): boolean {
    return this.isTransitioning;
  }

  cancelTransition(): void {
    this.isTransitioning = false;
    this.controls.enabled = this.controlsWasEnabled;
    this.onComplete = undefined;
  }

  moveToPose(pose: CameraPose, applyVerticalConstraint: boolean = true): void {
    this.applyPose(pose, applyVerticalConstraint);
  }

  transitionToPose(
    pose: CameraPose,
    duration: number,
    onComplete?: () => void,
    currentTime?: number,
    applyVerticalConstraint: boolean = true
  ): void {
    if (this.isTransitioning) {
      this.cancelTransition();
    }

    this.controlsWasEnabled = this.controls.enabled;
    this.controls.enabled = false;

    if (this.hasPose) {
      this.startPosition.copy(this.lastPosePosition);
      this.startTarget.copy(this.lastPoseTarget);
      this.startFov = this.lastPoseFov || this.camera.fov;
    } else {
      const currentDirection = new THREE.Vector3(0, 0, -1);
      currentDirection.applyQuaternion(this.camera.quaternion);
      const currentTarget = this.camera.position.clone().add(currentDirection);
      this.controls.lookAt(currentTarget);
      this.startPosition.copy(this.camera.position);
      this.startTarget.copy(currentTarget);
      this.startFov = this.camera.fov;
    }

    this.endPosition.copy(pose.position);
    this.endTarget.copy(pose.target);
    this.endFov = pose.fov ?? this.camera.fov;
    this.transitionApplyVerticalConstraint = applyVerticalConstraint;

    this.transitionDuration = Math.max(duration, 0.0001);
    this.transitionStartTime = currentTime ?? -1;
    this.isTransitioning = true;
    this.onComplete = onComplete;
  }

  setOrbitAnimationEnabled(enabled: boolean): void {
    this.orbitAnimationEnabled = enabled;
    if (!enabled) {
      this.stopOrbitAnimation();
    }
  }

  startOrbitAnimation(config: OrbitAnimationConfig): void {
    if (!this.orbitAnimationEnabled) {
      return;
    }
    this.orbitAnimationConfig = { ...config, sightPosition: config.sightPosition.clone() };
    this.orbitAnimationActive = true;
    this.orbitAnimationStartTime = -1;
    this.orbitAnimationBlendStartTime = -1;
    this.orbitAnimationBlendStartPosition.copy(this.camera.position);
    this.orbitAnimationBlendStartTarget.copy(this.controls.orbit.target);
    this.orbitAnimationPhaseOffset = this.findClosestOrbitPhase(config);
    this.updateOrbitAnimationBlendDuration();
    this.controls.setOrbitAnimationActive(true);
    this.orbitAnimationTarget.copy(config.sightPosition);
  }

  stopOrbitAnimation(): void {
    if (!this.orbitAnimationActive) {
      return;
    }
    this.orbitAnimationActive = false;
    this.orbitAnimationStartTime = -1;
    this.orbitAnimationBlendStartTime = -1;
    this.orbitAnimationConfig = null;
    this.orbitAnimationPhaseOffset = 0;
    this.controls.setOrbitAnimationActive(false);
  }

  setCameraConfig(config: CameraConfig): void {
    this.cameraConfig = { ...config };
    this.applyCameraConstraints();
  }

  private updateTransition(currentTime: number): void {
    if (this.transitionStartTime < 0) {
      this.transitionStartTime = currentTime;
    }
    const elapsed = currentTime - this.transitionStartTime;
    const progress = Math.min(elapsed / this.transitionDuration, 1.0);
    const easedProgress = this.easeInOutCubic(progress);

    const interpolatedPosition = new THREE.Vector3().lerpVectors(
      this.startPosition,
      this.endPosition,
      easedProgress
    );

    const currentTarget = new THREE.Vector3().lerpVectors(
      this.startTarget,
      this.endTarget,
      easedProgress
    );

    const fov =
      this.startFov !== this.endFov
        ? THREE.MathUtils.lerp(this.startFov, this.endFov, easedProgress)
        : this.camera.fov;

    this.applyPose(
      { position: interpolatedPosition, target: currentTarget, fov },
      this.transitionApplyVerticalConstraint
    );

    if (progress >= 1.0) {
      this.isTransitioning = false;
      this.controls.enabled = this.controlsWasEnabled;
      const complete = this.onComplete;
      if (complete) {
        complete();
        this.onComplete = undefined;
      }
    }
  }

  private updateOrbitAnimation(currentTime: number): void {
    if (!this.orbitAnimationConfig) {
      return;
    }

    const {
      sightPosition,
      distance,
      height,
      azimuthMin,
      azimuthMax,
      polarMin,
      polarMax,
    } = this.orbitAnimationConfig;

    const azimuthMid = (azimuthMin + azimuthMax) * 0.5;
    const azimuthAmplitude = (azimuthMax - azimuthMin) * 0.5;
    const polarMid = (polarMin + polarMax) * 0.5;
    const polarAmplitude = (polarMax - polarMin) * 0.5;
    const angularSpeed = (Math.PI * 2) / this.orbitAnimationPeriod;
    const phase =
      (currentTime - this.orbitAnimationStartTime) * angularSpeed + this.orbitAnimationPhaseOffset;

    const azimuth = azimuthMid + Math.cos(phase) * azimuthAmplitude;
    const polar = polarMid + Math.sin(phase) * polarAmplitude;

    const x = distance * Math.sin(polar) * Math.cos(azimuth);
    const y = distance * Math.cos(polar) + height;
    const z = distance * Math.sin(polar) * Math.sin(azimuth);

    this.orbitAnimationPosition.set(
      sightPosition.x + x,
      sightPosition.y + y,
      sightPosition.z + z
    );
    let nextPosition = this.orbitAnimationPosition;
    if (this.orbitAnimationBlendStartTime < 0) {
      this.orbitAnimationBlendStartTime = currentTime;
    }
    const blendElapsed = currentTime - this.orbitAnimationBlendStartTime;
    if (blendElapsed < this.orbitAnimationBlendDuration) {
      const blendProgress = Math.min(
        blendElapsed / Math.max(this.orbitAnimationBlendDuration, 0.0001),
        1
      );
      const eased = this.easeInOutCubic(blendProgress);
      this.orbitAnimationBlendedPosition.lerpVectors(
        this.orbitAnimationBlendStartPosition,
        this.orbitAnimationPosition,
        eased
      );
      nextPosition = this.orbitAnimationBlendedPosition;
    }

    let nextTarget = this.orbitAnimationTarget;
    if (blendElapsed < this.orbitAnimationBlendDuration) {
      const blendProgress = Math.min(
        blendElapsed / Math.max(this.orbitAnimationBlendDuration, 0.0001),
        1
      );
      const eased = this.easeInOutCubic(blendProgress);
      this.orbitAnimationBlendedTarget.lerpVectors(
        this.orbitAnimationBlendStartTarget,
        this.orbitAnimationTarget,
        eased
      );
      nextTarget = this.orbitAnimationBlendedTarget;
    }

    this.applyPose({ position: nextPosition, target: nextTarget }, false);
  }

  private applyPose(pose: CameraPose, applyVerticalConstraint: boolean = true): void {
    const constrainedPosition = pose.position.clone();
    if (
      this.cameraConfig.clampMinHeight !== -Infinity ||
      this.cameraConfig.clampMaxHeight !== Infinity
    ) {
      constrainedPosition.y = THREE.MathUtils.clamp(
        constrainedPosition.y,
        this.cameraConfig.clampMinHeight,
        this.cameraConfig.clampMaxHeight
      );
    }

    let constrainedTarget = pose.target.clone();
    if (applyVerticalConstraint && this.cameraConfig.constrainVertical) {
      constrainedTarget = this.constrainTargetVerticalAngle(
        constrainedPosition,
        constrainedTarget
      );
    }

    this.camera.position.copy(constrainedPosition);
    this.controls.lookAt(constrainedTarget);
    this.controls.setOrbitTargetOverride(constrainedTarget);

    if (pose.fov !== undefined && pose.fov !== this.camera.fov) {
      this.camera.fov = pose.fov;
      this.camera.updateProjectionMatrix();
    }

    this.lastPosePosition.copy(constrainedPosition);
    this.lastPoseTarget.copy(constrainedTarget);
    this.lastPoseFov = pose.fov ?? this.camera.fov;
    this.hasPose = true;
  }

  private applyCameraConstraints(): void {
    if (
      this.cameraConfig.clampMinHeight !== -Infinity ||
      this.cameraConfig.clampMaxHeight !== Infinity
    ) {
      this.camera.position.y = THREE.MathUtils.clamp(
        this.camera.position.y,
        this.cameraConfig.clampMinHeight,
        this.cameraConfig.clampMaxHeight
      );
    }

    if (this.cameraConfig.constrainVertical) {
      const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(
        this.camera.quaternion
      );
      const currentTarget = this.camera.position.clone().add(direction);
      const constrainedTarget = this.constrainTargetVerticalAngle(
        this.camera.position,
        currentTarget
      );
      this.controls.lookAt(constrainedTarget);
    }
  }

  private constrainTargetVerticalAngle(
    cameraPosition: THREE.Vector3,
    target: THREE.Vector3
  ): THREE.Vector3 {
    const direction = new THREE.Vector3().subVectors(target, cameraPosition);
    const spherical = new THREE.Spherical();
    spherical.setFromVector3(direction);

    spherical.phi = THREE.MathUtils.clamp(
      spherical.phi,
      this.cameraConfig.verticalMin,
      this.cameraConfig.verticalMax
    );

    const constrainedDirection = new THREE.Vector3().setFromSpherical(spherical);
    return cameraPosition
      .clone()
      .add(constrainedDirection.normalize().multiplyScalar(direction.length()));
  }

  private findClosestOrbitPhase(config: OrbitAnimationConfig): number {
    const sampleCount = 48;
    const twoPi = Math.PI * 2;
    const azimuthMid = (config.azimuthMin + config.azimuthMax) * 0.5;
    const azimuthAmplitude = (config.azimuthMax - config.azimuthMin) * 0.5;
    const polarMid = (config.polarMin + config.polarMax) * 0.5;
    const polarAmplitude = (config.polarMax - config.polarMin) * 0.5;
    let bestPhase = 0;
    let bestDistanceSq = Infinity;

    for (let i = 0; i < sampleCount; i += 1) {
      const phase = (i / sampleCount) * twoPi;
      const azimuth = azimuthMid + Math.cos(phase) * azimuthAmplitude;
      const polar = polarMid + Math.sin(phase) * polarAmplitude;
      const x = config.distance * Math.sin(polar) * Math.cos(azimuth);
      const y = config.distance * Math.cos(polar) + config.height;
      const z = config.distance * Math.sin(polar) * Math.sin(azimuth);
      const distanceSq = this.camera.position.distanceToSquared(
        this.orbitAnimationPosition.set(
          config.sightPosition.x + x,
          config.sightPosition.y + y,
          config.sightPosition.z + z
        )
      );
      if (distanceSq < bestDistanceSq) {
        bestDistanceSq = distanceSq;
        bestPhase = phase;
      }
    }

    return bestPhase;
  }

  private updateOrbitAnimationBlendDuration(): void {
    if (!this.orbitAnimationConfig) {
      return;
    }
    const { sightPosition, distance, height, azimuthMin, azimuthMax, polarMin, polarMax } =
      this.orbitAnimationConfig;
    const azimuthMid = (azimuthMin + azimuthMax) * 0.5;
    const azimuthAmplitude = (azimuthMax - azimuthMin) * 0.5;
    const polarMid = (polarMin + polarMax) * 0.5;
    const polarAmplitude = (polarMax - polarMin) * 0.5;
    const azimuth = azimuthMid + Math.cos(this.orbitAnimationPhaseOffset) * azimuthAmplitude;
    const polar = polarMid + Math.sin(this.orbitAnimationPhaseOffset) * polarAmplitude;
    const x = distance * Math.sin(polar) * Math.cos(azimuth);
    const y = distance * Math.cos(polar) + height;
    const z = distance * Math.sin(polar) * Math.sin(azimuth);

    this.orbitAnimationPosition.set(
      sightPosition.x + x,
      sightPosition.y + y,
      sightPosition.z + z
    );

    const blendDistance = this.orbitAnimationPosition.distanceTo(this.camera.position);
    const angularSpeed = (Math.PI * 2) / this.orbitAnimationPeriod;
    const orbitSpeed = Math.max(Math.abs(angularSpeed * Math.max(distance, 0.001)), 0.001);
    this.orbitAnimationBlendDuration = blendDistance / orbitSpeed;
  }

  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
}
